/**
 * Typography — extracted from Figma text styles on landing 58:39161.
 *
 * Families in use:
 *   Manrope        — headings, body, labels
 *   Aleo           — italic accents ("generational wealth", card titles)
 *   Space Grotesk  — button labels
 *   DM Sans        — resource list link
 */
import type { TypographyVariantsOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    display: React.CSSProperties;
    displayItalic: React.CSSProperties;
    eyebrow: React.CSSProperties;
    buttonLabel: React.CSSProperties;
    fieldLabel: React.CSSProperties;
    linkStrong: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    display?: React.CSSProperties;
    displayItalic?: React.CSSProperties;
    eyebrow?: React.CSSProperties;
    buttonLabel?: React.CSSProperties;
    fieldLabel?: React.CSSProperties;
    linkStrong?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
    displayItalic: true;
    eyebrow: true;
    buttonLabel: true;
    fieldLabel: true;
    linkStrong: true;
  }
}

export const fontFamilies = {
  sans: '"Manrope", system-ui, -apple-system, "Segoe UI", sans-serif',
  displaySerif: '"Aleo", Georgia, "Times New Roman", serif',
  button: '"Space Grotesk", "Manrope", system-ui, sans-serif',
  link: '"DM Sans", "Manrope", system-ui, sans-serif',
  // Legal disclaimer bar/panel only — that component's own Figma type
  // styles specify Inter, not the app's usual Manrope.
  legal: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extraBold: 800,
} as const;

export const typographyOptions: TypographyVariantsOptions = {
  fontFamily: fontFamilies.sans,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightBold: fontWeights.bold,

  // Hero title — Manrope Medium 64 / 56
  display: {
    fontFamily: fontFamilies.sans,
    fontSize: '4rem',
    lineHeight: '3.5rem',
    letterSpacing: 0,
    fontWeight: fontWeights.medium,
  },
  // Accent — Aleo Medium Italic 64, primary blue
  displayItalic: {
    fontFamily: fontFamilies.displaySerif,
    fontStyle: 'italic',
    fontSize: '4rem',
    lineHeight: '3.5rem',
    letterSpacing: 0,
    fontWeight: fontWeights.medium,
  },
  // Madlib heading — Manrope Medium 48 / 48
  h1: {
    fontFamily: fontFamilies.sans,
    fontSize: '3rem',
    lineHeight: 1,
    letterSpacing: 0,
    fontWeight: fontWeights.medium,
  },
  // Carousel / resources section titles — Manrope Medium 40–44
  h2: {
    fontFamily: fontFamilies.sans,
    fontSize: '2.5rem',
    lineHeight: '3rem',
    letterSpacing: 0,
    fontWeight: fontWeights.medium,
  },
  // Madlib sentence / mission prompt — Manrope Medium 24–32
  h3: {
    fontFamily: fontFamilies.sans,
    fontSize: '1.5rem',
    lineHeight: '2rem',
    letterSpacing: 0,
    fontWeight: fontWeights.medium,
  },
  // Hero body — Manrope Regular 17 / 25.5, tracking -0.17
  body1: {
    fontFamily: fontFamilies.sans,
    fontSize: '1.0625rem',
    lineHeight: '1.59375rem',
    letterSpacing: '-0.01em',
    fontWeight: fontWeights.regular,
  },
  // Card body — Manrope Regular 14–16
  body2: {
    fontFamily: fontFamilies.sans,
    fontSize: '0.875rem',
    lineHeight: '1.195rem',
    fontWeight: fontWeights.regular,
  },
  // MUI Button base — Space Grotesk (Figma button labels)
  button: {
    fontFamily: fontFamilies.button,
    fontSize: '1rem',
    lineHeight: 1.275,
    fontWeight: fontWeights.medium,
    textTransform: 'none',
    letterSpacing: 0,
  },
  buttonLabel: {
    fontFamily: fontFamilies.button,
    fontSize: '1rem',
    lineHeight: 1.275,
    fontWeight: fontWeights.medium,
    textTransform: 'none',
    letterSpacing: 0,
  },
  // Search field labels — Manrope ExtraBold 16 / 24, #2C3A61
  fieldLabel: {
    fontFamily: fontFamilies.sans,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    fontWeight: fontWeights.extraBold,
    letterSpacing: 0,
    textTransform: 'none',
  },
  // Resource link — DM Sans Bold 16
  linkStrong: {
    fontFamily: fontFamilies.link,
    fontSize: '1rem',
    lineHeight: '1.3rem',
    fontWeight: fontWeights.bold,
    letterSpacing: 0,
  },
  eyebrow: {
    fontFamily: fontFamilies.sans,
    fontSize: '0.875rem',
    lineHeight: '1.195rem',
    letterSpacing: '-0.01em',
    textTransform: 'none',
    fontWeight: fontWeights.extraBold,
  },
};
